import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ManualSection from '../ManualSection';
import './story-analysis-manual.scss';

/**
 * `/readme/story-analysis` manual content (TRIL-425), on the TRIL-424 framework.
 *
 * HARD content rule (anti-gaming, the reason this manual is auth-gated): teach
 * principles, not the formula. Explain what each signal is *getting at* and how
 * to read it — never the exact scoring weights, model prompts, or thresholds.
 * Keep it honest about the analyzer's limits (it's a directional signal, not a
 * verdict) without publishing anything that turns it into a gaming manual.
 *
 * The dimensions below mirror the real PostAnalysis sections (Overall quality,
 * Tone, Emotions, Topic relevance, Readability & quality, Integrity, Score
 * composition, Findings) — conceptual language only.
 */
export default function StoryAnalysisManual() {
  const { hash } = useLocation();

  // Non-expandable blocks are always visible; the framework's <ManualSection>
  // handles auto-expand for hash-targeted dimensions. This just scrolls a
  // top-level block into view when it's the deep-link target.
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    el?.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [hash]);

  return (
    <div className="SA__MANUAL">
      <p className="MANUAL__LEAD">
        When you analyze a story, trillirivos gives you a{' '}
        <strong>second read</strong> — an automated look at how your writing
        lands across a few dimensions. It&apos;s here to help you see your work
        from the outside and decide what to sharpen.
      </p>

      <div className="SA__CAVEAT" id="what-it-is">
        <span className="SA__CAVEAT__MK">Read this first</span>
        <p>
          It&apos;s a <b>signal, not a verdict.</b> The analysis is directional
          — it can miss nuance, and it can&apos;t judge whether your ideas are{' '}
          <em>right</em>. Treat it as a prompt to look again, not a grade. Trust
          your own judgment on the call.
        </p>
      </div>

      <section className="SA__BLOCK" id="what-we-value">
        <h2>What good writing means here</h2>
        <p className="SA__SUB">
          Substance, clarity, and honesty: a piece that develops a real idea,
          reads clearly, and doesn&apos;t mislead. Each signal below gets at one
          slice of that — none of them is the whole picture.
        </p>
      </section>

      <section className="SA__BLOCK" id="signals">
        <h2>What the analysis looks at</h2>
        <p className="SA__SUB">
          Open any one for what it&apos;s getting at and how to read it.
        </p>

        <ManualSection id="overall" title="Overall quality" defaultOpen>
          <p>
            A single high-level read that blends the signals below into one
            number. It rewards a piece that feels developed, clear, and sound.
            Read it as a rough altitude, not a precise grade — the{' '}
            <b>direction it moves as you edit</b> tells you more than the exact
            value on any given draft.
          </p>
        </ManualSection>

        <ManualSection id="tone" title="Tone &amp; emotion">
          <p>
            The emotional colouring of the piece — how positive, negative, or
            neutral it reads, and which emotions come through. This is{' '}
            <b>descriptive, not good-or-bad</b>: a hard-hitting piece is
            <em>supposed</em> to read intense. Use it to check that the tone
            matches what you intended.
          </p>
        </ManualSection>

        <ManualSection id="topic" title="Topic &amp; focus">
          <p>
            How tightly the piece stays on its subject. A focused piece scores
            high here; a low read can mean it wanders or tries to do too much.
            Read it as a nudge to check that every part earns its place — not a
            rule that a piece must stick to one note.
          </p>
        </ManualSection>

        <ManualSection id="readability" title="Clarity &amp; readability">
          <p>
            How easily the writing carries a reader — reading ease, grammar, and
            structure. It rewards clear, well-formed prose. One caveat:{' '}
            <b>simpler isn&apos;t automatically better.</b> It flags friction;
            you decide whether a dense passage is doing deliberate work or just
            getting in the way.
          </p>
        </ManualSection>

        <ManualSection id="integrity" title="Integrity signals">
          <p>
            Honesty checks rather than quality scores: whether a piece reads as
            likely AI-generated, leans on manipulative framing, makes claims
            worth fact-checking, looks original, or looks like spam. These exist
            to keep the feed trustworthy and can feed moderation — they are{' '}
            <b>not a meter to optimise.</b> The fix is always the same: write
            honestly and in your own voice.
          </p>
        </ManualSection>

        <ManualSection id="composition" title="Score composition">
          <p>
            A breakdown of which signals moved the overall read, so the number
            isn&apos;t a black box. Use it to see <em>why</em> a piece landed
            where it did — which is far more useful than the score alone.
          </p>
        </ManualSection>

        <ManualSection id="findings" title="Findings for you">
          <p>
            Specific, piece-level observations and suggestions — the most
            actionable part of the analysis, and the one part shown only to you,
            on your own stories. If you read one thing, read this: it points at
            concrete spots to revisit rather than an abstract number.
          </p>
        </ManualSection>
      </section>

      <section className="SA__BLOCK" id="reading-it">
        <h2>How to use it</h2>
        <p className="SA__SUB">
          Use it to improve, not to chase a number. The score will wobble as you
          edit — that&apos;s normal. Fix what the findings point at, keep the
          tone you meant, and don&apos;t sand off your voice to please a meter.
          A piece written for its reader tends to read well here as a
          side-effect; a piece written for the meter usually doesn&apos;t.
        </p>
      </section>

      <div className="SA__NOTE" id="why-no-formula">
        <span className="SA__NOTE__MK">Why we don&apos;t show the formula</span>
        <p>
          We deliberately don&apos;t publish the exact weights, prompts, or
          thresholds behind these signals. Not to be mysterious — but because a
          published formula becomes a recipe to game, and a piece optimised for
          the recipe reads worse, not better. Write for your reader; the
          analysis follows.
        </p>
      </div>
    </div>
  );
}
